import { CheckCircle2, HelpCircle, KeyIcon, Loader2, MailIcon } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/app/components/ui/button";
import { Dialog, DialogContent } from "@/app/components/ui/dialog.jsx";
import { Input } from "@/app/components/ui/input";
import { useAuth } from "@/app/context/Auth/AuthContext";
import { useCompanyClaim } from "@/app/hooks/useCompanyClaim.js";
import { decodeClaimToken, setActiveManagedCompanyId } from "@/app/services/companies/claimSession.js";

export const ClaimModal = ({
	open,
	onOpenChange,
	companyId,
	companyName,
	initialEmail,
	initialStep,
	isCompanyClaimed,
}) => {
	const navigate = useNavigate();
	const { setSession } = useAuth();
	const [step, setStep] = useState(initialStep || "email");
	const [email, setEmail] = useState(initialEmail || "");
	const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
	const inputRefs = useRef([]);
	const { requestClaimOTP, verifyClaimOTP, isLoading } = useCompanyClaim();

	const handleSendVerification = async () => {
		if (!email) return;

		const payload = {
			companyId: companyId,
			email: email,
		};

		const success = await requestClaimOTP(payload);
		if (success) {
			setStep("verification");
		}
	};

	const handleVerify = async () => {
		const code = verificationCode.join("");
		if (code.length !== 6) return;

		const payload = {
			companyId: companyId,
			email: email,
			otp: code,
		};

		const response = await verifyClaimOTP(payload);
		if (response?.token) {
			setSession({ token: response.token, expiresAt: response.expiresAt });
			if (decodeClaimToken(response.token)?.role === "admin") {
				setActiveManagedCompanyId(companyId);
			}
			onOpenChange(false);
			navigate("/company/dashboard", { replace: true });
		}
	};

	const handleClose = () => {
		onOpenChange(false);
		// Reset after animation completes
		setTimeout(() => {
			setStep(initialStep || "email");
			setEmail(initialEmail || "");
			setVerificationCode(["", "", "", "", "", ""]);
		}, 300);
	};
	const handleCodeChange = (index, value) => {
		if (value.length > 1) {
			value = value[0];
		}

		if (!/^\d*$/.test(value)) return;

		const newCode = [...verificationCode];
		newCode[index] = value;
		setVerificationCode(newCode);

		// Autofocus next input
		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index, e) => {
		if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text").slice(0, 6);
		if (!/^\d+$/.test(pastedData)) return;

		const newCode = [...verificationCode];
		pastedData.split("").forEach((char, index) => {
			if (index < 6) {
				newCode[index] = char;
			}
		});
		setVerificationCode(newCode);

		// Focus the next empty input or the last one
		const nextEmptyIndex = newCode.findIndex((val) => !val);
		if (nextEmptyIndex !== -1) {
			inputRefs.current[nextEmptyIndex]?.focus();
		} else {
			inputRefs.current[5]?.focus();
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[800px] bg-white p-12">
				{step === "email" && (
					<div className="flex flex-col items-center gap-8">
						<div className="w-[60px] h-[60px] bg-anima-light-blue-bg rounded-3xl flex items-center justify-center">
							<MailIcon className="w-8 h-8 text-primary-700" strokeWidth={1.5} />
						</div>

						<div className="flex flex-col gap-3 text-center">
							<h2 className="text-[32px] font-bold text-anima-dark-text font-['Plus_Jakarta_Sans',Helvetica] leading-tight">
								{`${isCompanyClaimed ? "Login to" : "Claim"} ${companyName}`}
							</h2>
							<p className="text-base text-[#6c757d] font-['Inter',Helvetica] leading-relaxed">
								Enter your company email to verify that you work at this company.
							</p>
						</div>

						<div className="flex flex-col gap-2 w-full">
							<label className="text-base font-semibold text-anima-dark-text font-['Inter',Helvetica]">
								Company Email
							</label>
							<div className="relative">
								<Input
									type="email"
									placeholder="Enter company email address"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									className="w-full h-14 px-4 pr-12 bg-white rounded-xl border border-gray-300 font-['Inter',Helvetica] text-base text-gray-700 placeholder:text-gray-500 focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:border-primary-700"
									disabled={isLoading}
								/>
								<HelpCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
							</div>
						</div>

						<div className="flex items-center gap-4 w-full pt-4">
							<Button
								onClick={handleClose}
								variant="outline"
								disabled={isLoading}
								className="flex-1 h-14 bg-white text-anima-dark-text rounded-xl font-['Inter',Helvetica] font-semibold text-base hover:bg-gray-50"
								style={{ border: "1px solid #dee2e6" }}
							>
								Cancel
							</Button>
							<Button
								onClick={handleSendVerification}
								disabled={!email || isLoading}
								className="flex-1 h-14 bg-primary-700 hover:bg-primary-700/90 text-white rounded-xl shadow-none font-['Inter',Helvetica] font-semibold text-base disabled:opacity-50"
							>
								{isLoading ? (
									<>
										<Loader2 className="w-5 h-5 animate-spin" />
										Sending...
									</>
								) : (
									"Send Verification Code"
								)}
							</Button>
						</div>
					</div>
				)}

				{step === "verification" && (
					<div className="flex flex-col items-center gap-8">
						<div className="w-[60px] h-[60px] bg-anima-light-blue-bg rounded-3xl flex items-center justify-center">
							<KeyIcon className="w-8 h-8 text-primary-700" strokeWidth={1.5} />
						</div>

						<div className="flex flex-col gap-3 text-center">
							<h2 className="text-[32px] font-bold text-anima-dark-text font-['Plus_Jakarta_Sans',Helvetica] leading-tight">
								Enter verification code
							</h2>
							<p className="text-base text-[#6c757d] font-['Inter',Helvetica] leading-relaxed">
								We've sent a 6-digit code to <span className="font-semibold text-anima-dark-text">{email}</span>
							</p>
						</div>

						<div className="flex items-center justify-center gap-3 w-full">
							{verificationCode.map((digit, index) => (
								<input
									key={index}
									ref={(el) => (inputRefs.current[index] = el)}
									type="text"
									inputMode="numeric"
									maxLength={1}
									value={digit}
									onChange={(e) => handleCodeChange(index, e.target.value)}
									onKeyDown={(e) => handleKeyDown(index, e)}
									onPaste={index === 0 ? handlePaste : undefined}
									disabled={isLoading}
									className="w-16 h-16 text-center text-xl font-semibold bg-white border-2 border-gray-300 rounded-2xl font-['Inter',Helvetica] text-anima-dark-text focus:outline-hidden focus:ring-2 focus:ring-primary-700 focus:border-primary-700 disabled:opacity-50"
								/>
							))}
						</div>

						<p className="text-base text-[#6c757d] font-['Inter',Helvetica]">
							Didn't get a code?{" "}
							<button
								onClick={handleSendVerification}
								disabled={isLoading}
								className="text-primary-700 underline hover:text-[#0856b3] font-medium"
							>
								Click to resend
							</button>
							.
						</p>

						<div className="flex items-center gap-4 w-full pt-4">
							<Button
								onClick={handleClose}
								variant="outline"
								disabled={isLoading}
								className="flex-1 h-14 bg-white border-gray-300 text-anima-dark-text rounded-xl font-['Inter',Helvetica] font-semibold text-base hover:bg-gray-50"
								style={{ border: "1px solid #dee2e6" }}
							>
								Cancel
							</Button>
							<Button
								onClick={handleVerify}
								disabled={verificationCode.join("").length !== 6 || isLoading}
								className="flex-1 h-14 bg-primary-700 hover:bg-primary-700/90 text-white rounded-xl shadow-none font-['Inter',Helvetica] font-semibold text-base disabled:opacity-50"
							>
								{isLoading ? (
									<>
										<Loader2 className="w-5 h-5 animate-spin" />
										Verifying...
									</>
								) : (
									"Verify & Continue"
								)}
							</Button>
						</div>

						<button
							onClick={() => setStep("email")}
							disabled={isLoading}
							className="text-base text-[#6c757d] hover:text-anima-dark-text font-['Inter',Helvetica] font-medium underline"
						>
							Use a different email
						</button>
					</div>
				)}

				{step === "success" && (
					<div className="flex flex-col items-center gap-8 py-4">
						<div className="w-[60px] h-[60px] bg-anima-light-blue-bg rounded-3xl flex items-center justify-center">
							<CheckCircle2 className="w-8 h-8 text-primary-700" strokeWidth={1.5} />
						</div>

						<div className="flex flex-col gap-4 text-center">
							<h2 className="text-[32px] font-bold text-anima-dark-text font-['Plus_Jakarta_Sans',Helvetica] leading-tight">
								You're verified!
							</h2>
							<p className="text-base text-[#6c757d] font-['Inter',Helvetica] leading-relaxed">
								Congratulations! You now have full control of your company page on Dproz.
							</p>
						</div>

						<div className="flex items-center gap-4 p-6 bg-gray-50 rounded-2xl w-full">
							<div className="w-16 h-16 bg-primary-700 rounded-full flex items-center justify-center shrink-0">
								{/* <img
                  src="https://c.animaapp.com/mkfndw4v3NChDU/img/logo-grayscale.png"
                  alt="ABC Logistics"
                  className="w-12 h-12 object-contain"
                /> */}
								{/* Placeholder for company logo image, as the original path is external */}
								<span className="text-white text-xl font-bold">{companyName?.substring(0, 3)?.toUpperCase()}</span>
							</div>
							<div className="flex flex-col items-start gap-1 flex-1">
								<div className="flex items-center gap-2">
									<h3 className="text-xl font-bold text-anima-dark-text font-['Inter',Helvetica]">{companyName}</h3>
									<div className="flex items-center gap-1 px-2 py-1 bg-anima-light-blue-bg rounded-lg">
										<CheckCircle2 className="w-4 h-4 text-primary-700" />
										<span className="text-sm font-semibold text-primary-700 font-['Inter',Helvetica]">Verified</span>
									</div>
								</div>
								<p className="text-base text-[#6c757d] font-['Inter',Helvetica]">{email}</p>
							</div>
						</div>

						<div className="flex flex-col gap-0 w-full pt-4">
							<Button
								onClick={handleClose}
								className="w-full h-14 text-white rounded-xl shadow-none font-['Inter',Helvetica] font-semibold text-base"
								style={{ backgroundColor: "#0a6fe2", hoverBackgroundColor: "#0856b3" }}
							>
								Go to Dashboard
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};
